import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';

// Google Analytics tracking component
const GoogleAnalytics = () => {
  const location = useLocation();
  
  useEffect(() => {
    if (typeof window.gtag !== 'undefined') {
      window.gtag('config', 'G-75029C5Y1Y', {
        page_path: location.pathname + location.search,
      });
    }
  }, [location]);
  
  return null;
};

// Lazy load components
const HomePage = lazy(() => import('./pages/HomePage'));
const ShopPage = lazy(() => import('./pages/ShopPage'));
const ShopPageOutOfStock = lazy(() => import('./pages/ShopPageOutOfStock'));
const AdminLogin = lazy(() => import('./pages/AdminLogin'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const OrderConfirmation = lazy(() => import('./pages/OrderConfirmation'));
const StockManagement = lazy(() => import('./pages/StockManagement'));
const OrderPacking = lazy(() => import('./pages/OrderPacking'));
const TrackOrder = lazy(() => import('./pages/TrackOrder'));
const ProductImport = lazy(() => import('./pages/ProductImport'));
const OrderStockAnalysis = lazy(() => import('./pages/OrderStockAnalysis'));
const BillingPage = lazy(() => import('./pages/BillingPage'));
const VetriVelanBilling = lazy(() => import('./pages/VetriVelanBilling'));
const SivakasiFirworks = lazy(() => import('./pages/SivakasiFirworks'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const LuckyDraw = lazy(() => import('./pages/LuckyDraw'));
const AgentPricing = lazy(() => import('./pages/AgentPricing'));
const CreateBillPage = lazy(() => import('./pages/CreateBillPage'));
const EditOrder = lazy(() => import('./pages/EditOrder'));
const ApiKeysPage = lazy(() => import('./pages/ApiKeysPage'));
const CreateAgentPage = lazy(() => import('./pages/CreateAgentPage'));
const AgentBillImportPage = lazy(() => import('./pages/AgentBillImportPage'));
const OrderAlertPage = lazy(() => import('./pages/OrderAlertPage'));
const ProductAnalyticsPage = lazy(() => import('./pages/ProductAnalyticsPage'));
const ToDespatchPage = lazy(() => import('./pages/ToDespatchPage'));
const ProcurementPage = lazy(() => import('./pages/ProcurementPage'));
const ProcurementImportPage = lazy(() => import('./pages/ProcurementImportPage'));
const BusinessAnalyticsPage = lazy(() => import('./pages/BusinessAnalyticsPage'));
const DisclaimerModal = lazy(() => import('./components/DisclaimerModal'));

// Loading component
const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
      <p className="mt-4 text-gray-600">Loading...</p>
    </div>
  </div>
);

function App() {
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [cart, setCart] = useState(() => {
    // Load cart from sessionStorage on initial render
    try {
      const savedCart = sessionStorage.getItem('cart');
      return savedCart ? JSON.parse(savedCart) : [];
    } catch (error) {
      console.error('Error loading cart from sessionStorage:', error);
      sessionStorage.removeItem('cart');
      return [];
    }
  });

  useEffect(() => {
    const disclaimerAccepted = localStorage.getItem('disclaimerAccepted');
    if (!disclaimerAccepted) {
      setShowDisclaimer(true);
    }
  }, []);

  const handleDisclaimerAccept = () => {
    localStorage.setItem('disclaimerAccepted', 'true');
    setShowDisclaimer(false);
  };

  const saveCartToStorage = (cartData) => {
    try {
      // Store only essential cart data
      const minimalCart = cartData.map(item => ({
        id: item.id,
        name: item.name,
        price: item.price,
        quantity: item.quantity
      }));
      sessionStorage.setItem('cart', JSON.stringify(minimalCart));
    } catch (error) {
      if (error.name === 'QuotaExceededError') {
        sessionStorage.clear();
        try {
          const minimalCart = cartData.map(item => ({
            id: item.id,
            name: item.name,
            price: item.price,
            quantity: item.quantity
          }));
          sessionStorage.setItem('cart', JSON.stringify(minimalCart));
        } catch (secondError) {
          console.error('Cart storage failed:', secondError);
        }
      }
    }
  };

  const addToCart = (product, quantity) => {
    const existingItem = cart.find(item => item.id === product.id);
    let newCart;
    if (existingItem) {
      const newQuantity = existingItem.quantity + quantity;
      if (newQuantity > 999) {
        alert('Maximum quantity per item is 999');
        return;
      }
      newCart = cart.map(item => 
        item.id === product.id 
          ? {...item, quantity: newQuantity}
          : item
      );
    } else {
      if (cart.length >= 100) {
        alert('Maximum 100 different items allowed in cart');
        return;
      }
      newCart = [...cart, {...product, quantity}];
    }
    setCart(newCart);
    saveCartToStorage(newCart);
  };

  const updateCartQuantity = (productId, quantity) => {
    let newCart;
    if (quantity === 0) {
      newCart = cart.filter(item => item.id !== productId);
    } else {
      if (quantity > 999) {
        alert('Maximum quantity per item is 999');
        return;
      }
      newCart = cart.map(item => 
        item.id === productId ? {...item, quantity} : item
      );
    }
    setCart(newCart);
    saveCartToStorage(newCart);
  };

  const clearCart = () => {
    setCart([]);
    sessionStorage.removeItem('cart');
  };

  return (
    <AuthProvider>
      <Router>
        <GoogleAnalytics />
        <div className="min-h-screen bg-gray-100">
          <Suspense fallback={<LoadingFallback />}>
            <DisclaimerModal isOpen={showDisclaimer} onAccept={handleDisclaimerAccept} />
          </Suspense>
          <Routes>
            <Route path="/" element={
              <Suspense fallback={<LoadingFallback />}>
                <HomePage />
              </Suspense>
            } />
            <Route path="/shop" element={
              <Suspense fallback={<LoadingFallback />}>
                { <ShopPage 
                  cart={cart}
                  addToCart={addToCart}
                  updateCartQuantity={updateCartQuantity}
                  clearCart={clearCart}
                /> }
				{/* <ShopPageOutOfStock 
                  cart={cart}
                  addToCart={addToCart}
                  updateCartQuantity={updateCartQuantity}
                  clearCart={clearCart}
                />*/}
              </Suspense>
            } />
            <Route path="/checkout" element={
              <Suspense fallback={<LoadingFallback />}>
                <CheckoutPage 
                  cart={cart}
                  clearCart={clearCart}
                />
              </Suspense>
            } />
            <Route path="/enquiry-confirmation/:token" element={
              <Suspense fallback={<LoadingFallback />}>
                <OrderConfirmation />
              </Suspense>
            } />
            <Route path="/track-order" element={
              <Suspense fallback={<LoadingFallback />}>
                <TrackOrder />
              </Suspense>
            } />
            <Route path="/admin/login" element={
              <Suspense fallback={<LoadingFallback />}>
                <AdminLogin />
              </Suspense>
            } />
            <Route path="/admin/dashboard" element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingFallback />}>
                  <AdminDashboard />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/stock" element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingFallback />}>
                  <StockManagement />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/pack/:orderId" element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingFallback />}>
                  <OrderPacking />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/import-products" element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingFallback />}>
                  <ProductImport />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/stock-analysis" element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingFallback />}>
                  <OrderStockAnalysis />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/billing" element={
              <ProtectedRoute requiredRoles={['admin', 'billing']}>
                <Suspense fallback={<LoadingFallback />}>
                  <BillingPage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/create-bill" element={
              <ProtectedRoute requiredRoles={['admin', 'billing']}>
                <Suspense fallback={<LoadingFallback />}>
                  <CreateBillPage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/vetrivelan-billing" element={
              <Suspense fallback={<LoadingFallback />}>
                <VetriVelanBilling />
              </Suspense>
            } />
            <Route path="/admin/agent-pricing" element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingFallback />}>
                  <AgentPricing />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/edit-order/:orderId" element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingFallback />}>
                  <EditOrder />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/api-keys" element={
              <ProtectedRoute requiredRoles={['admin']}>
                <Suspense fallback={<LoadingFallback />}>
                  <ApiKeysPage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/create-agent" element={
              <ProtectedRoute requiredRoles={['admin']}>
                <Suspense fallback={<LoadingFallback />}>
                  <CreateAgentPage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/agent-bill-import" element={
              <ProtectedRoute requiredRoles={['admin']}>
                <Suspense fallback={<LoadingFallback />}>
                  <AgentBillImportPage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/order-alert/:orderId" element={
              <ProtectedRoute requiredRoles={['admin', 'billing']}>
                <Suspense fallback={<LoadingFallback />}>
                  <OrderAlertPage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/product-analytics" element={
              <ProtectedRoute requiredRoles={['admin']}>
                <Suspense fallback={<LoadingFallback />}>
                  <ProductAnalyticsPage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/to-despatch" element={
              <ProtectedRoute requiredRoles={['admin', 'billing']}>
                <Suspense fallback={<LoadingFallback />}>
                  <ToDespatchPage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/procurement" element={
              <ProtectedRoute requiredRoles={['admin']}>
                <Suspense fallback={<LoadingFallback />}>
                  <ProcurementPage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/procurement/import" element={
              <ProtectedRoute requiredRoles={['admin']}>
                <Suspense fallback={<LoadingFallback />}>
                  <ProcurementImportPage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/admin/business-analytics" element={
              <ProtectedRoute requiredRoles={['admin']}>
                <Suspense fallback={<LoadingFallback />}>
                  <BusinessAnalyticsPage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/sivakasi-crackers" element={
              <Suspense fallback={<LoadingFallback />}>
                <SivakasiFirworks />
              </Suspense>
            } />
            <Route path="/contact" element={
              <Suspense fallback={<LoadingFallback />}>
                <ContactPage />
              </Suspense>
            } />
            <Route path="/lucky-draw" element={
              <Suspense fallback={<LoadingFallback />}>
                <LuckyDraw />
              </Suspense>
            } />
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;